import GDAvatar from "@/components/Avatar/GDAvatar";
import GdLoading from "@/components/GdLoading";
import HBIcon from "@/components/HBIcon";
import HBMessage from "@/components/HBMessage";
import { HBModalProps } from "@/components/HBModal";
import ModalWrap from "@/components/HBModal/ModalWrap";
import Image from "@/components/Image";
import { isVipByUser } from "@/constants/vip";
import useAppStore from "@/context/store";
import { gdImgSrcPolicy, gdOssUrl } from "@/utils/imageUrl";
import { checkLogin } from "@/utils/open";
import { useMemoizedFn, useMount } from "ahooks";
import { Button, Modal } from "antd";
import classNames from "classnames";
import { useMemo, useState } from "react";
import GdMergeInterimState from "../MergeInterimState";
import { OrgSelect } from "../OrgSelect";
import { GdMergeStatus, goGaodingAuth, ORGS_CLASS_MAP } from "../shared";
import { useGdMerge } from "../useGdmerge";

import styles from "./index.module.less";

function InitStep({ pageConf, onCreateAccount, gdUser }) {
  const [haveAccountButtonHover, setHaveAccountButtonHover] = useState(false);

  const haveAccountButtonContent = useMemo(() => {
    if (gdUser && haveAccountButtonHover) {
      return (
        <>
          <GDAvatar shape="circle" size={24} imgUrl={gdUser.avatar} />
          <span>{gdUser?.nick}</span>
        </>
      );
    }

    return gdUser ? <>我有稿定账号</> : "我有稿定账号";
  }, [gdUser, haveAccountButtonHover]);

  return (
    <>
      <div className={styles.title}>升级账号，解锁稿定全新创作体验</div>
      <div className={styles.desc}>
        快捷创作工具、优质设计模板、AI创作赋能，让你的灵感转化为现实
      </div>

      {pageConf?.bannerVideo?.length > 0 ? (
        <video
          src={pageConf.bannerVideo?.[0]?.url}
          width={600}
          muted
          autoPlay
          loop
          {...(pageConf.bannerVideoPoster?.[0]?.url
            ? { poster: gdOssUrl(pageConf.bannerVideoPoster?.[0]?.url) }
            : null)}
        ></video>
      ) : (
        pageConf?.bannerImage?.length > 0 && (
          <Image
            {...gdImgSrcPolicy(pageConf.bannerImage[0]?.url, {
              displayWidth: 600,
            })}
            width={600}
            height={360}
            alt="banner"
          />
        )
      )}

      {gdUser ? (
        <div className={styles.footer}>
          <Button
            className={styles.button}
            onClick={onCreateAccount}
            data-gd-click="button_click"
            data-button-name="未使用过稿定"
            data-subject-name="按钮"
          >
            未使用过稿定
          </Button>
          <Button
            className={classNames(styles.button, styles.primary)}
            onClick={() => goGaodingAuth(true)}
            onMouseEnter={() => {
              setHaveAccountButtonHover(true);
            }}
            onMouseLeave={() => {
              setHaveAccountButtonHover(false);
            }}
            data-gd-click="button_click"
            data-button-name="我有稿定账号"
            data-subject-name="按钮"
          >
            {haveAccountButtonContent}
          </Button>
        </div>
      ) : (
        <div className={styles.footer}>
          <Button
            data-gd-click="button_click"
            data-button-name="我有稿定账号"
            data-subject-name="按钮"
            className={styles.button}
            onClick={() => goGaodingAuth(false)}
          >
            我有稿定账号
          </Button>
          <Button
            className={classNames(styles.button, styles.primary)}
            onClick={onCreateAccount}
            data-gd-click="button_click"
            data-button-name="未使用过稿定"
            data-subject-name="按钮"
          >
            未使用过稿定
          </Button>
        </div>
      )}
    </>
  );
}

function OrgSelectStep({ gdUser, orgs, onMerge }) {
  const user = useAppStore((s) => s.user);
  const [selectedOrg, setSelectedOrg] = useState(null);

  const orgClass = useMemo(() => {
    return isVipByUser(user) ? ORGS_CLASS_MAP.ORG : ORGS_CLASS_MAP.PERSONAL;
  }, [user]);

  const onSubmit = useMemoizedFn(async () => {
    if (selectedOrg === null) {
      HBMessage.error("请选择租户");
      return;
    }

    const user = await checkLogin();

    let org = selectedOrg;
    if (!selectedOrg && orgClass === ORGS_CLASS_MAP.PERSONAL) {
      org = gdUser?.orgs?.find(
        (org) => org.company.org_class === ORGS_CLASS_MAP.PERSONAL
      );
    }

    onMerge({
      user_id: gdUser?.user_id,
      is_init_org: !org,
      init_org_type: orgClass,
      hb_user_name: user?.username,
      ...(org ? { org_id: org.org_id } : null),
      code: gdUser?.code,
    });
  });

  return (
    <>
      <div className={styles.title}>与你的团队成员共享花瓣素材权益</div>
      <div className={styles.desc}>
        共享后，若你拥有花瓣素材权益，该团队内的成员都可以在花瓣平台下载素材
      </div>
      <OrgSelect
        className={styles.orgs}
        orgs={orgs}
        onChange={(org) => {
          setSelectedOrg(org);
        }}
        value={selectedOrg}
        orgClass={orgClass}
      />
      <div className={classNames(styles.footer, styles.orgSelectfooter)}>
        <div className="color-text-2">
          已登录账号：{gdUser?.user_name}
          <a
            className="gd-blue-text-button"
            onClick={() => goGaodingAuth(false)}
          >
            更换
          </a>
        </div>
        <Button
          className={classNames(styles.button, styles.primary)}
          onClick={onSubmit}
        >
          确认
        </Button>
      </div>
    </>
  );
}

function MergingStep() {
  return (
    <div>
      <div className={styles.title}>账号升级</div>
      <GdLoading className={styles.loading} size={140} />
      <div className={styles.loadingText}>正在处理中...</div>
    </div>
  );
}

function GdMergeModal(
  props: HBModalProps & { pageConf: any; needAutoMerge?: boolean }
) {
  useMount(() => {
    window.___gaoding_account_callback = (code) => {
      window.__gaoding_account_window.close();
      onGaodingAuthCallback(code);
    };
  });

  const {
    step,
    sameTelGdUser,
    authGdUserData,
    checkResult,
    onGaodingAuthCallback,
    onMerge,
    onCreateAccount,
    setStep,
  } = useGdMerge({
    needAutoMerge: props.needAutoMerge,
    onMergeFail: () => {
      props?.onCancel();
    },
    onMergeSuccess: () => {
      props.onOk();
    },
  });

  const closable = useMemo(() => {
    // 正在合并loading中，不可关闭弹窗
    return step !== GdMergeStatus.merging;
  }, [step]);

  return (
    <Modal
      {...props}
      centered
      width={640}
      className={styles.modal}
      closeIcon={<HBIcon type="chacha" />}
      footer={null}
      closable={closable}
      maskClosable={false}
      keyboard={false}
    >
      <div data-gd-expose="dialog_expose" data-dialog-title="账号融合弹窗">
        {step === GdMergeStatus.init && (
          <InitStep
            gdUser={sameTelGdUser}
            pageConf={props.pageConf}
            onCreateAccount={onCreateAccount}
          />
        )}
        {step === GdMergeStatus.selectOrg && (
          <OrgSelectStep
            orgs={authGdUserData?.orgs}
            gdUser={authGdUserData?.user}
            onMerge={onMerge}
          />
        )}
        {step === GdMergeStatus.interim && (
          <GdMergeInterimState
            checkResult={checkResult}
            onGoOn={() => {
              onGaodingAuthCallback(checkResult?.temp_code, "MERGE");
            }}
            onCancel={() => {
              setStep(GdMergeStatus.init);
            }}
          />
        )}
        {step === GdMergeStatus.merging && <MergingStep />}
      </div>
    </Modal>
  );
}

export const GDMergeDialog = ModalWrap(GdMergeModal, { unique: true });
